import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: "advisor" | "client" | "executor" | "administrator";
  mustChangePassword?: boolean;
  emailVerified?: boolean;
  disclaimerAcknowledgedAt?: string | null;
  isFAndF?: boolean;
  ffExplainerSeen?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<any>;
  signup: (email: string, password: string, fullName: string, role: "advisor" | "client", phoneNumber: string) => Promise<void>;
  /** Signs out, then a full reload to the public landing page (s90). Takes NO argument on purpose: it is
   *  passed straight to onClick handlers (AdvisorSettings, AdvisorNav via UserMenu), which hand it an event. */
  logout: () => Promise<void>;
  /** s226 — the same sign-out, landing on `to` instead. The board's "Sign out" controls (the interview strip,
   *  the home header) land on the sign-in page (Reade). One mutation, one POST /api/auth/logout. */
  logoutTo: (to: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useQuery<User>({
    queryKey: ["/api/auth/me"],
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const loginMutation = useMutation({
    // Raw fetch (not apiRequest) so we can read the structured 403 body for the
    // email-verification gate — apiRequest throws on non-OK before we can parse it.
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        const err = new Error(error.error || "Login failed");
        (err as any).emailVerificationRequired = error.emailVerificationRequired === true;
        throw err;
      }
      return await res.json() as User;
    },
    onSuccess: async () => {
      // Cancel any in-flight queries (e.g. the previous user's /api/clients fetch)
      // BEFORE clearing — otherwise a late resolution can repopulate the cache for
      // the wrong account. cancelQueries marks them cancelled (their resolution is
      // ignored even without an abort signal); clear() then wipes the cache.
      await queryClient.cancelQueries();
      queryClient.clear();
    },
  });

  const signupFlight = useSingleFlight();

  const signupMutation = useMutation({
    mutationFn: async ({ email, password, fullName, role, phoneNumber }: {
      email: string;
      password: string;
      fullName: string;
      role: "advisor" | "client";
      phoneNumber: string;
    }) => {
      const res = await apiRequest("POST", "/api/auth/signup", { email, password, fullName, role, phoneNumber });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Signup failed");
      }
      return await res.json() as User;
    },
    onSuccess: async () => {
      // Cancel in-flight queries before clearing — see loginMutation note.
      await queryClient.cancelQueries();
      queryClient.clear();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async (_to: string) => {
      const res = await apiRequest("POST", "/api/auth/logout");
      if (!res.ok) {
        throw new Error("Logout failed");
      }
    },
    onSuccess: (_data, to) => {
      // Land on the public landing page after logout (s90), not /login. A full reload
      // to "/" wipes all client cache on its own — so we deliberately do NOT clear()
      // here: clearing would flip isAuthenticated and let the route guard redirect to
      // /login before the browser navigates to "/" (a race). Leaving the cache intact
      // keeps the guard dormant until the reload replaces everything.
      window.location.href = to;
    },
  });

  const login = async (email: string, password: string) => {
    // Returns the parsed response so callers can branch on an MFA challenge
    // ({ mfaRequired, mfaToken, phoneLastFour }) vs a real authenticated User.
    return await loginMutation.mutateAsync({ email, password });
  };

  const signup = async (email: string, password: string, fullName: string, role: "advisor" | "client", phoneNumber: string) => {
    // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire attempts to create an account (and, for a
    // non-bypass address, send a verification email). `run` rethrows, so the signup form's own
    // error handling is unchanged; a duplicate resolves undefined and does nothing.
    await signupFlight.run(() => signupMutation.mutateAsync({ email, password, fullName, role, phoneNumber }));
  };

  const logout = async () => {
    await logoutMutation.mutateAsync("/");
  };
  const logoutTo = async (to: string) => {
    await logoutMutation.mutateAsync(to);
  };

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        logout,
        logoutTo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** s226 — where the board's "Sign out" controls land (Reade: "lands on the sign-in page"). */
export const SIGN_IN_PATH = "/login";

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
